import { useEffect, useRef } from 'react';
import { AuthenticatedPhoto } from '@/components/photographs/AuthenticatedPhoto';
import type { BlockReportPhoto } from '@/lib/types';

const UNASSIGNED_LABEL = 'UNASSIGNED';
const UNASSIGNED_BLOCK_NAME = 'No Block / Unassigned';

export interface ProjectBlock {
  id: string;
  name: string;
}

function mergeReportTexts(values: (string | null | undefined)[]): string {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const value of values) {
    const s = String(value || '').replace(/\s+/g, ' ').trim();
    if (!s) continue;
    const key = s.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(s);
  }
  return out.join('; ');
}

interface PendingBlockRecord {
  id: string;
  blockId: string | null;
  blockName: string | null;
}

interface PendingRequirementRecord extends PendingBlockRecord {
  requiredSkilled: number;
  requiredUnskilled: number;
  typeOfWork: string | null;
  locationOfWork: string | null;
  descriptionOfWork: string | null;
}

interface PendingActualRecord extends PendingBlockRecord {
  actualSkilled: number;
  actualUnskilled: number;
  typeOfWork: string | null;
  locationOfWork: string | null;
  descriptionOfWork: string | null;
  actualWorkDone: string | null;
  remarks: string | null;
  photos: BlockReportPhoto[];
}

interface BlockGroup<T> {
  key: string;
  label: string | null;
  records: T[];
}

function groupByBlock<T extends PendingBlockRecord>(items: T[], projectBlocks?: ProjectBlock[]): BlockGroup<T>[] {
  const raw = new Map<string, T[]>();
  for (const item of items) {
    const key = item.blockId || '__unassigned__';
    const list = raw.get(key) || [];
    list.push(item);
    raw.set(key, list);
  }

  const unassigned: T[] = [];
  const byBlockId = new Map<string, T[]>();
  for (const [key, list] of raw) {
    if (key === '__unassigned__') {
      unassigned.push(...list);
    } else {
      byBlockId.set(key, list);
    }
  }
  const nameById = new Map((projectBlocks ?? []).map((b) => [b.id, b.name]));
  const groups: BlockGroup<T>[] = [];
  for (const [blockId, list] of byBlockId) {
    const blockName = nameById.get(blockId) ?? list[0].blockName ?? blockId;
    groups.push({ key: blockId, label: blockName, records: list });
  }
  groups.sort((a, b) => (a.label || '').localeCompare(b.label || ''));
  if (unassigned.length > 0) {
    groups.push({ key: UNASSIGNED_LABEL, label: null, records: unassigned });
  }
  return groups;
}

export interface ApprovalRequirementRow {
  key: string;
  blockName: string;
  recordIds: string[];
  requiredSkilled: number;
  requiredUnskilled: number;
  typeOfWork: string;
  locationOfWork: string;
  descriptionOfWork: string;
  hasData: boolean;
}

export interface ApprovalActualRow {
  key: string;
  blockName: string;
  recordIds: string[];
  actualSkilled: number;
  actualUnskilled: number;
  typeOfWork: string;
  locationOfWork: string;
  descriptionOfWork: string;
  actualWorkDone: string;
  remarks: string;
  photos: BlockReportPhoto[];
  hasData: boolean;
}

function uniquePhotos(records: PendingActualRecord[]): BlockReportPhoto[] {
  const seen = new Set<string>();
  const out: BlockReportPhoto[] = [];
  for (const record of records) {
    for (const photo of record.photos || []) {
      if (seen.has(photo.photo_id)) continue;
      seen.add(photo.photo_id);
      out.push(photo);
    }
  }
  return out;
}

export function buildRequirementRows(items: PendingRequirementRecord[], projectBlocks?: ProjectBlock[]): ApprovalRequirementRow[] {
  return groupByBlock(items, projectBlocks).flatMap((group) => {
    const records = group.records;
    if (!group.label) {
      if (records.length === 0) return [];
      return [{
        key: UNASSIGNED_LABEL,
        blockName: UNASSIGNED_BLOCK_NAME,
        recordIds: records.map((r) => r.id),
        requiredSkilled: records.reduce((s, r) => s + (r.requiredSkilled || 0), 0),
        requiredUnskilled: records.reduce((s, r) => s + (r.requiredUnskilled || 0), 0),
        typeOfWork: mergeReportTexts(records.map((r) => r.typeOfWork)),
        locationOfWork: mergeReportTexts(records.map((r) => r.locationOfWork)),
        descriptionOfWork: mergeReportTexts(records.map((r) => r.descriptionOfWork)),
        hasData: true,
      }];
    }
    return [{
      key: group.key,
      blockName: group.label,
      recordIds: records.map((r) => r.id),
      requiredSkilled: records.reduce((s, r) => s + (r.requiredSkilled || 0), 0),
      requiredUnskilled: records.reduce((s, r) => s + (r.requiredUnskilled || 0), 0),
      typeOfWork: mergeReportTexts(records.map((r) => r.typeOfWork)),
      locationOfWork: mergeReportTexts(records.map((r) => r.locationOfWork)),
      descriptionOfWork: mergeReportTexts(records.map((r) => r.descriptionOfWork)),
      hasData: records.length > 0,
    }];
  });
}

export function buildActualRows(items: PendingActualRecord[], projectBlocks?: ProjectBlock[]): ApprovalActualRow[] {
  return groupByBlock(items, projectBlocks).flatMap((group) => {
    const records = group.records;
    const base = {
      recordIds: records.map((r) => r.id),
      actualSkilled: records.reduce((s, r) => s + (r.actualSkilled || 0), 0),
      actualUnskilled: records.reduce((s, r) => s + (r.actualUnskilled || 0), 0),
      typeOfWork: mergeReportTexts(records.map((r) => r.typeOfWork)),
      locationOfWork: mergeReportTexts(records.map((r) => r.locationOfWork)),
      descriptionOfWork: mergeReportTexts(records.map((r) => r.descriptionOfWork)),
      actualWorkDone: mergeReportTexts(records.map((r) => r.actualWorkDone)),
      remarks: mergeReportTexts(records.map((r) => r.remarks)),
      photos: uniquePhotos(records),
    };
    if (!group.label) {
      if (records.length === 0) return [];
      return [{
        ...base,
        key: UNASSIGNED_LABEL,
        blockName: UNASSIGNED_BLOCK_NAME,
        hasData: true,
      }];
    }
    return [{
      ...base,
      key: group.key,
      blockName: group.label,
      hasData: records.length > 0,
    }];
  });
}

function ApproveCheckbox({
  checked,
  indeterminate,
  disabled,
  label,
  onChange,
}: {
  checked: boolean;
  indeterminate?: boolean;
  disabled?: boolean;
  label: string;
  onChange: () => void;
}) {
  const ref = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (ref.current) ref.current.indeterminate = !!indeterminate;
  }, [indeterminate]);
  return (
    <input
      ref={ref}
      type="checkbox"
      checked={checked}
      disabled={disabled}
      aria-label={label}
      onChange={onChange}
      className="h-4 w-4 cursor-pointer align-middle"
    />
  );
}

interface ApprovalSheetProps {
  projectName: string;
  selectedIds: Set<string>;
  onToggleRecordIds: (recordIds: string[]) => void;
  allSelected: boolean;
  partial: boolean;
  onToggleAll: () => void;
}

function numberCell(value: number): string {
  return Number.isFinite(value) ? String(value) : '';
}

function PhotoCell({ photos }: { photos: BlockReportPhoto[] }) {
  if (!photos || photos.length === 0) return null;
  const shown = photos.slice(0, 4);
  return (
    <div className="flex flex-wrap items-center justify-center gap-1">
      {shown.map((p) => (
        <AuthenticatedPhoto
          key={p.photo_id}
          src={`/actual-status/${p.entity_id}/photographs/${p.photo_id}`}
          alt={p.file_name || 'Work photograph'}
          className="block h-[84px] w-[110px] border border-black bg-white object-contain p-0.5"
          placeholderClassName="h-[84px] w-[110px]"
        />
      ))}
    </div>
  );
}

function RequirementCheckCell({ row, selectedIds, onToggle }: { row: ApprovalRequirementRow; selectedIds: Set<string>; onToggle: (ids: string[]) => void }) {
  const checked = row.recordIds.some((id) => selectedIds.has(id));
  const partialChecked = checked && !row.recordIds.every((id) => selectedIds.has(id));
  return (
    <td className="print-check-cell">
      <ApproveCheckbox
        checked={checked}
        indeterminate={partialChecked}
        disabled={row.recordIds.length === 0}
        label={`Select ${row.blockName}`}
        onChange={() => onToggle(row.recordIds)}
      />
    </td>
  );
}

function ActualCheckCell({ row, selectedIds, onToggle }: { row: ApprovalActualRow; selectedIds: Set<string>; onToggle: (ids: string[]) => void }) {
  const checked = row.recordIds.some((id) => selectedIds.has(id));
  const partialChecked = checked && !row.recordIds.every((id) => selectedIds.has(id));
  return (
    <td className="print-check-cell">
      <ApproveCheckbox
        checked={checked}
        indeterminate={partialChecked}
        disabled={row.recordIds.length === 0}
        label={`Select ${row.blockName}`}
        onChange={() => onToggle(row.recordIds)}
      />
    </td>
  );
}

export function ApprovalRequirementSheet({ projectName, rows, selectedIds, onToggleRecordIds, allSelected, partial, onToggleAll }: ApprovalSheetProps & { rows: ApprovalRequirementRow[] }) {
  return (
    <div className="print-report-sheet pending-approval-report">
      <table className="print-table">
        <colgroup>
          <col style={{ width: '10%' }} />
          <col style={{ width: '12%' }} />
          <col style={{ width: '14%' }} />
          <col style={{ width: '14%' }} />
          <col style={{ width: '17%' }} />
          <col style={{ width: '17%' }} />
          <col style={{ width: '16%' }} />
        </colgroup>
        <thead>
          <tr>
            <th className="print-title" colSpan={7}>APPROVAL OF LABOUR REQUIREMENT REPORT FORMAT</th>
          </tr>
          <tr>
            <td className="print-subhead" colSpan={7}>Project: {projectName}</td>
          </tr>
          <tr>
            <th rowSpan={2}>APPROVAL CHECK/SELECTION BLOCK</th>
            <th rowSpan={2}>Block</th>
            <th colSpan={2}>REQUIRED LABOUR</th>
            <th rowSpan={2}>TYPE OF WORK</th>
            <th rowSpan={2}>LOCATION OF WORK</th>
            <th rowSpan={2}>DESCRIPTION OF WORK</th>
          </tr>
          <tr>
            <th>SKILLED</th>
            <th>UN-SKILLED</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.key} className={row.recordIds.some((id) => selectedIds.has(id)) ? 'print-row-selected' : undefined}>
              <RequirementCheckCell row={row} selectedIds={selectedIds} onToggle={onToggleRecordIds} />
              <td className="print-block-cell">{row.blockName}</td>
              <td>{numberCell(row.requiredSkilled)}</td>
              <td>{numberCell(row.requiredUnskilled)}</td>
              <td>{row.typeOfWork || ''}</td>
              <td>{row.locationOfWork || ''}</td>
              <td>{row.descriptionOfWork || ''}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="mt-2">
        <ApproveCheckbox
          checked={allSelected}
          indeterminate={partial}
          disabled={!rows.some((r) => r.recordIds.length > 0)}
          label="Select all blocks"
          onChange={onToggleAll}
        />
        <span className="ml-2 text-xs text-secondary">Select all pending rows for this project and report</span>
      </div>
    </div>
  );
}

export function ApprovalActualSheet({ projectName, rows, selectedIds, onToggleRecordIds, allSelected, partial, onToggleAll }: ApprovalSheetProps & { rows: ApprovalActualRow[] }) {
  return (
    <div className="print-report-sheet pending-approval-report">
      <table className="print-table">
        <colgroup>
          <col style={{ width: '8%' }} />
          <col style={{ width: '6%' }} />
          <col style={{ width: '7%' }} />
          <col style={{ width: '7%' }} />
          <col style={{ width: '11%' }} />
          <col style={{ width: '11%' }} />
          <col style={{ width: '12%' }} />
          <col style={{ width: '12%' }} />
          <col style={{ width: '18%' }} />
          <col style={{ width: '8%' }} />
        </colgroup>
        <thead>
          <tr>
            <th className="print-title" colSpan={10}>ACTUAL LABOUR RECEIVED REPORT FORMAT</th>
          </tr>
          <tr>
            <td className="print-subhead" colSpan={10}>Project: {projectName}</td>
          </tr>
          <tr>
            <th rowSpan={2}>REVIEW CHECK/SELECTION BLOCK</th>
            <th rowSpan={2}>Block</th>
            <th colSpan={2}>ACTUAL LABOUR</th>
            <th rowSpan={2}>TYPE OF WORK</th>
            <th rowSpan={2}>LOCATION OF WORK</th>
            <th rowSpan={2}>DESCRIPTION OF WORK</th>
            <th rowSpan={2}>ACTUAL WORK DONE DISCRIPTION</th>
            <th rowSpan={2}>PHOTOGRAPH OF ACTUAL WORK DONE</th>
            <th rowSpan={2}>REMARKS IF ANY</th>
          </tr>
          <tr>
            <th>SKILLED</th>
            <th>UN-SKILLED</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.key} className={row.recordIds.some((id) => selectedIds.has(id)) ? 'print-row-selected' : undefined}>
              <ActualCheckCell row={row} selectedIds={selectedIds} onToggle={onToggleRecordIds} />
              <td className="print-block-cell">{row.blockName}</td>
              <td>{numberCell(row.actualSkilled)}</td>
              <td>{numberCell(row.actualUnskilled)}</td>
              <td>{row.typeOfWork || ''}</td>
              <td>{row.locationOfWork || ''}</td>
              <td>{row.descriptionOfWork || ''}</td>
              <td>{row.actualWorkDone || ''}</td>
              <td><PhotoCell photos={row.photos} /></td>
              <td>{row.remarks || ''}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="mt-2">
        <ApproveCheckbox
          checked={allSelected}
          indeterminate={partial}
          disabled={!rows.some((r) => r.recordIds.length > 0)}
          label="Select all blocks"
          onChange={onToggleAll}
        />
        <span className="ml-2 text-xs text-secondary">Select all pending rows for this project and report</span>
      </div>
    </div>
  );
}